/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import {
  UserIcon,
  UserGroupIcon,
  CogIcon,
  ComputerDesktopIcon,
  WrenchScrewdriverIcon,
  TagIcon,
  WrenchIcon
} from "@heroicons/react/24/outline";

import Layout from "src/components/layouts/Layout.react";
import Users from "src/views/settings/users/Users.react";
import Technicians from "src/views/settings/Technicians.react";
import WindTurbines from "src/views/settings/WindTurbines.react";
import WindFarms from "src/views/settings/WindFarms.react";
import SystemConfig from "src/views/settings/SystemConfig.react";
import Maker from "src/views/settings/Maker.react";
import Tool from "src/views/settings/Tool.react";
import Calibration from "src/views/settings/Calibration.react";

/**
 * The User Management Page component
 *
 * @author Thais Watanabe
 */
const SettingsPage = () => {
  const [selectedOption, setSelectedOption] = useState("admin");

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout title="設定">
      <main className="relative -mt-32">
        <div className="max-w-screen-xl mx-auto">
          <div className="bg-white rounded-lg shadow">
            <div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
              <aside className="py-6 lg:col-span-2">
                <nav className="space-y-1">
                  <h3
                    className="pl-3 pb-2 text-sm font-medium text-gray-500"
                  >
                    システム管理
                  </h3>

                  <button
                    onClick={() => setSelectedOption("admin")}
                    className={`${
                      selectedOption === "admin"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                    aria-current="page"
                  >
                    {/* <!-- Heroicon name: user --> */}
                    <UserIcon
                      className={`${
                        selectedOption === "admin"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">ユーザー</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("technician")}
                    className={`${
                      selectedOption === "technician"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: user-group --> */}
                    <UserGroupIcon
                      className={`${
                        selectedOption === "technician"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">作業者</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("windfarm")}
                    className={`${
                      selectedOption === "windfarm"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: cog --> */}
                    <CogIcon
                      className={`${
                        selectedOption === "windfarm"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">発電所</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("windturbine")}
                    className={`${
                      selectedOption === "windturbine"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: cog --> */}
                    <CogIcon
                      className={`${
                        selectedOption === "windturbine"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">風車</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("system")}
                    className={`${
                      selectedOption === "system"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: desktop-computer --> */}
                    <ComputerDesktopIcon
                      className={`${
                        selectedOption === "system"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">システム情報</span>
                  </button>

                  <h3 className="pl-3 pt-5 pb-2 text-sm font-medium text-gray-500">
                    工具管理
                  </h3>

                  <button
                    onClick={() => setSelectedOption("calibration")}
                    className={`${
                      selectedOption === "calibration"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: desktop-computer --> */}
                    <WrenchIcon
                      className={`${
                        selectedOption === "calibration"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">機材校正</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("tool")}
                    className={`${
                      selectedOption === "tool"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: desktop-computer --> */}
                    <WrenchScrewdriverIcon
                      className={`${
                        selectedOption === "tool"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">工具</span>
                  </button>

                  <button
                    onClick={() => setSelectedOption("maker")}
                    className={`${
                      selectedOption === "maker"
                        ? "bg-teal-50 border-teal-500 text-teal-700 hover:bg-teal-50 hover:text-teal-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                    } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full`}
                  >
                    {/* <!-- Heroicon name: desktop-computer --> */}
                    <TagIcon
                      className={`${
                        selectedOption === "maker"
                          ? "text-teal-500 group-hover:text-teal-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } flex-shrink-0 -ml-1 mr-3 h-6 w-6`}
                    />
                    <span className="truncate">メーカー</span>
                  </button>
                </nav>
              </aside>

              {/* <!-- Side section --> */}
              <div className="divide-y divide-gray-200 lg:col-span-10">
                <div className="py-6">
                  {selectedOption === "admin" && <Users />}
                  {selectedOption === "technician" && <Technicians />}
                  {selectedOption === "windturbine" && <WindTurbines />}
                  {selectedOption === "windfarm" && <WindFarms />}
                  {selectedOption === "calibration" && <Calibration />}
                  {selectedOption === "maker" && <Maker />}
                  {selectedOption === "tool" && <Tool />}
                  {selectedOption === "system" && <SystemConfig />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default SettingsPage;
